import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Button from "../../components/Shared/Button";
import Input from "../../components/Shared/Input";
import Selectbox, { option } from "../../components/Shared/SelectBox";
import Upload from "../../components/Shared/Upload";
import { toast } from "react-toastify";
import Spinner from "../../components/Shared/Spinner";
import { getCourses } from "../../api/Courses/GetAllCourses";
import { GetCourseById } from "../../api/Courses/GetCourseById";
import { getLectures } from "../../api/Lecture/GetAllLectures";
import { UpdateIFRSExam } from "../../api/Quiz/UpdateIFRSExam";
import { GetIFRSById } from "../../api/Quiz/GetIFRSById";
import { GenerateIFRSExamId } from "../../api/Quiz/GenerateIFRSExamId";
import { ButtonTypes } from "../../utils/consts";
import { getChapters } from "../../api/Chapters/GetAllChapters";

const CreateIFRSExam = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id, courseId } = useParams<{ id?: string; courseId?: string }>();
  const isEditMode = !!id;

  const [title, setTitle] = useState("");
  const [selectedCourse, setSelectedCourse] = useState<option | null>(null);
  const [selectedLecture, setSelectedLecture] = useState<option | null>(null);
  const [selectedVideo, setSelectedVideo] = useState<option | null>(null);
  const [duration, setDuration] = useState("");
  const [modelAnswerUrl, setModelAnswerUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedLectureChapterId, setSelectedLectureChapterId] = useState<
    string | null
  >(null);
  const [generatedExamId, setGeneratedExamId] = useState<string | null>(null);

  // Fetch exam data if in edit mode
  const { data: ExamResponse, isLoading: isExamLoading } = useQuery({
    queryFn: () => GetIFRSById(id || ""),
    queryKey: ["ifrs", id],
    enabled: isEditMode && !!id,
    staleTime: 0,
    refetchInterval: 0,
    refetchOnWindowFocus: false,
  });

  // Fetch all courses
  const { data: CoursesResponse, isLoading: isCoursesLoading } = useQuery({
    queryFn: getCourses,
    queryKey: ["courses"],
    staleTime: 0,
    refetchInterval: 0,
    refetchOnWindowFocus: false,
  });

  // Fetch all lectures
  const { data: LecturesResponse, isLoading: isLecturesLoading } = useQuery({
    queryFn: getLectures,
    queryKey: ["lectures"],
    staleTime: 0,
    refetchInterval: 0,
    refetchOnWindowFocus: false,
  });

  const courses = CoursesResponse?.data?.coursesData || [];
  const courseOptions = courses.map((course) => ({
    id: course.id,
    slug: course.slug,
    name: course.name,
  }));

  const allLectures = LecturesResponse?.data || [];

  // Fetch course details when a course is selected to get chapter IDs
  const { data: CourseByIdDTO, isLoading: isCourseLoading } = useQuery({
    queryFn: () => GetCourseById(selectedCourse?.slug || ""),
    queryKey: ["course", selectedCourse?.slug],
    enabled: !!selectedCourse,
    staleTime: 0,
    refetchInterval: 0,
    refetchOnWindowFocus: false,
  });

  // Fetch chapters data to get videos
  const { data: chaptersData, isLoading: isChaptersLoading } = useQuery({
    queryFn: () => getChapters(CourseByIdDTO?.data?.id || ""),
    queryKey: ["chapters", CourseByIdDTO?.data?.id],
    enabled: !!CourseByIdDTO?.data?.id,
    staleTime: 0,
    refetchInterval: 0,
    refetchOnWindowFocus: false,
  });

  console.log({ chaptersDataLength:chaptersData?.data?.length });

  // Get chapter IDs from selected course
  const courseChapterIds =
    CourseByIdDTO?.data?.chapters?.map((ch: { id: string }) => ch.id) || [];

  // Filter lectures that belong to chapters in the selected course
  const courseLectures = allLectures.filter(
    (lecture) =>
      lecture.chapterId && courseChapterIds.includes(lecture.chapterId)
  );

  // Sort lectures by order
  const sortedLectures = [...courseLectures].sort((a, b) => a.order - b.order);

  const lectureOptions = sortedLectures.map((lecture) => ({
    id: lecture.id,
    name: lecture.title,
  }));

  // Get videos for selected lecture from chapters data
  const getVideosForLecture = () => {
    if (!selectedLecture?.id || !chaptersData) return [];

    // Find the lecture in the chapters data
    if (chaptersData?.data) 
      {for (let i = 0; i < chaptersData?.data.length; i++) {
      const lecture = chaptersData?.data[i].lectures?.find(
        (lec: any) => lec.id === selectedLecture.id
      );
      console.log({lecture})
      if (lecture && lecture.videos) {
        console.log({videos:lecture.videos})
        return lecture.videos;
      }
    }}
    return [];
  };

  const lectureVideos = getVideosForLecture();

  // Map videos to options using the video name from chapters data
  const videoOptions = lectureVideos.map((video: any) => ({
    id: video.id,
    name: video.name || `Video ${video.id}`,
  }));

  // Reset lecture when course changes
  const handleCourseChange = (course: option | null) => {
    setSelectedCourse(course);
    setSelectedLecture(null);
    setSelectedVideo(null);
    setSelectedLectureChapterId(null);
  };

  // Handle lecture selection and store its chapterId
  const handleLectureChange = (lecture: option | null) => {
    setSelectedLecture(lecture);
    setSelectedVideo(null); // Reset video when lecture changes
    if (lecture) {
      const lectureData = allLectures.find((lec) => lec.id === lecture.id);
      setSelectedLectureChapterId(lectureData?.chapterId || null);
    } else {
      setSelectedLectureChapterId(null);
    }
  };

  // Fetch course details for edit mode to populate dropdowns
  const [courseSlugForEdit, setCourseSlugForEdit] = useState<string | null>(
    null
  );

  const { data: EditCourseDTO } = useQuery({
    queryFn: () => GetCourseById(courseSlugForEdit || ""),
    queryKey: ["course", courseSlugForEdit],
    enabled: isEditMode && !!courseSlugForEdit,
    staleTime: 0,
    refetchInterval: 0,
    refetchOnWindowFocus: false,
  });

  // Populate form when in edit mode
  useEffect(() => {
    if (isEditMode && ExamResponse?.data && courses.length > 0) {
      const exam = ExamResponse.data;
      setTitle(exam.title);
      // Duration is already in minutes, no conversion needed
      setDuration(String(exam.duration));
      if (exam.modelAnswerUrl) {
        setModelAnswerUrl(exam.modelAnswerUrl);
      }
    }
    if (courses.length > 0) {
      if (courseId) {
        const course = courses.find((c) => c.id === courseId);
        if (course) {
          setSelectedCourse({
            id: course.id,
            slug: course.slug,
            name: course.name,
          });
          setCourseSlugForEdit(course.slug);
        }
      }
    }
  }, [isEditMode, ExamResponse, courses, courseId]);

  // Populate lecture and video when course, chapters, and lectures are loaded in edit mode
  useEffect(() => {
    if (
      isEditMode &&
      EditCourseDTO?.data &&
      ExamResponse?.data &&
      allLectures.length > 0 &&
      chaptersData
    ) {
      const exam = ExamResponse.data;

      // Set lecture
      const lecture = allLectures.find((lec) => lec.id === exam.lectureId);
      if (lecture) {
        setSelectedLecture({ id: lecture.id, name: lecture.title });
        setSelectedLectureChapterId(lecture.chapterId || null);

        // Set video if it exists - find it in chapters data
        if (exam.videoId && chaptersData?.data) {
          for (let i=0; i < chaptersData?.data?.length; i++) {
            const lectureInChapter = chaptersData.data[i].lectures?.find(
              (lec: any) => lec.id === exam.lectureId
            );
            if (lectureInChapter && lectureInChapter.videos) {
              const video = lectureInChapter.videos.find(
                (v: any) => v.id === exam.videoId
              );
              if (video) {
                setSelectedVideo({
                  id: video.id,
                  name: video.name || `Video ${video.id}`,
                });
                break;
              }
            }
          }
        }
      }
    }
  }, [isEditMode, EditCourseDTO, ExamResponse, allLectures, chaptersData]);

  // Generate exam ID mutation (for create mode only)
  const { mutate: generateExamId } = useMutation({
    mutationFn: async (formData: {
      title: string;
      chapterId: string;
      lectureId: string;
      duration: number;
      videoId?: string;
    }) => {
      // Use existing exam ID if available, otherwise generate a new one
      let examId = generatedExamId;

      if (!examId) {
        // Generate the exam ID only if we don't have one
        const generateResponse = await GenerateIFRSExamId();

        if (generateResponse.error) {
          throw new Error(
            generateResponse.error.message || "Failed to generate exam ID"
          );
        }

        const newExamId = generateResponse.data?.data?.id;
        if (!newExamId) {
          throw new Error("Failed to get exam ID");
        }

        examId = newExamId;
        // Store the generated exam ID for future use
        setGeneratedExamId(newExamId);
      }

      // Update the exam with user data using the exam ID
      const updateResponse = await UpdateIFRSExam(examId, formData);

      if (updateResponse.error) {
        throw new Error(
          updateResponse.error.message || "Failed to create IFRS Exam"
        );
      }

      return { examId, updateResponse };
    },
    onSuccess: () => {
      setIsSubmitting(false);
      toast.success("IFRS Exam created successfully");
      queryClient.invalidateQueries({ queryKey: ["ifrs-exams"] });
      navigate(`/dashboard/courses/${courseId}/ifrs-exam`);
    },
    onError: () => {
      setIsSubmitting(false);
      toast.error("Failed to create IFRS Exam");
    },
  });

  // Update exam mutation (for edit mode)
  const { mutate: updateExam } = useMutation({
    mutationFn: (data: {
      title: string;
      chapterId: string;
      lectureId: string;
      duration: number;
      videoId?: string;
    }) => {
      if (!id) throw new Error("Exam ID is required for update");
      return UpdateIFRSExam(id, data);
    },
    onSuccess: (response) => {
      if (response.error) {
        setIsSubmitting(false);
        toast.error(response.error.message || "Failed to update IFRS Exam");
        return;
      }
      setIsSubmitting(false);
      toast.success("IFRS Exam updated successfully");
      queryClient.invalidateQueries({ queryKey: ["ifrs-exams"] });
      queryClient.invalidateQueries({ queryKey: ["ifrs", id] });
      navigate(`/dashboard/courses/${courseId}/ifrs-exam`);
    },
    onError: () => {
      setIsSubmitting(false);
      toast.error("Failed to update IFRS Exam");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error("Please enter a title");
      return;
    }
    if (!selectedCourse) {
      toast.error("Please select a course");
      return;
    }
    if (!selectedLecture) {
      toast.error("Please select a lecture");
      return;
    }
    if (!selectedLectureChapterId) {
      toast.error("Selected lecture must belong to a chapter");
      return;
    }
    if (!duration.trim()) {
      toast.error("Please enter duration");
      return;
    }

    setIsSubmitting(true);
    const formData: {
      title: string;
      chapterId: string;
      lectureId: string;
      duration: number;
      videoId?: string;
      modelAnswerUrl?: string;
    } = {
      title,
      chapterId: selectedLectureChapterId,
      lectureId: selectedLecture.id || "",
      duration: Number(duration),
    };

    // Only add videoId if a video is selected
    if (selectedVideo?.id) {
      formData.videoId = selectedVideo.id;
    }

    // Only add modelAnswerUrl if one was uploaded
    if (modelAnswerUrl) {
      formData.modelAnswerUrl = modelAnswerUrl;
    }

    if (isEditMode) {
      // Edit mode: directly update the exam
      updateExam(formData);
    } else {
      // Create mode: first generate ID, then update
      generateExamId(formData);
    }
  };

  if (isCoursesLoading || isLecturesLoading || (isEditMode && isExamLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="py-7">
      <div className="flex items-center gap-4 mb-7">
        <Link
          to={`/dashboard/courses/${courseId}/ifrs-exam`}
          className="text-primary hover:text-primary/80 font-semibold"
        >
          ← Back to IFRS Exams
        </Link>
        <h1 className="text-burntBlack font-bold text-[32px]">
          {isEditMode ? "Edit IFRS Exam" : "Create IFRS Exam"}
        </h1>
      </div>

      <div className="p-8 rounded-xl bg-white border-black/15 shadow">
        <h2 className="font-bold text-2xl mb-6">Exam Information</h2>
        <form onSubmit={handleSubmit}>
          <div className="space-y-6 w-full">
            <Input
              name="title"
              type="text"
              title="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter exam title"
              inputStyles="!rounded !h-[50px]"
              isRequired
            />

            {!courseId && (
              <Selectbox
                title="Course"
                selectOptions={courseOptions}
                selected={selectedCourse}
                setSelected={handleCourseChange}
                placeholder="Select a course"
                className="!rounded"
              />
            )}

            {selectedCourse && (
              <Selectbox
                title="Lecture"
                selectOptions={lectureOptions}
                selected={selectedLecture}
                setSelected={handleLectureChange}
                placeholder={
                  isCourseLoading || isLecturesLoading
                    ? "Loading lectures..."
                    : lectureOptions.length === 0
                    ? "No lectures available for this course"
                    : "Select a lecture"
                }
                className="!rounded"
              />
            )}

              <Selectbox
                title="Video (Optional)"
                selectOptions={videoOptions}
                selected={selectedVideo}
                setSelected={setSelectedVideo}
                placeholder={
                  isChaptersLoading
                    ? "Loading videos..."
                    : "Select a video (optional)"
                }
                className="!rounded"
              />

            <Input
              name="duration"
              type="number"
              title="Duration (minutes)"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              placeholder="Enter duration in minutes"
              inputStyles="!rounded !h-[50px]"
              isRequired
            />

            <div className="w-full">
              <label className="block mb-2 font-medium text-burntBlack">
                Answer Model <span className="text-gray-400 text-sm font-normal">(Optional)</span>
              </label>
              <Upload
                folder="ifrs-model-answers"
                acceptedFiles={{ "application/pdf": [".pdf"] }}
                maxFiles={1}
                onUploadStarts={() => setIsUploading(true)}
                onUploadEnds={() => setIsUploading(false)}
                onFileUploaded={(url) => setModelAnswerUrl(url)}
                
              >
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-primary transition-colors">
                  {isUploading ? (
                    <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                      <Spinner />
                      <span>Uploading...</span>
                    </div>
                  ) : modelAnswerUrl ? (
                    <div className="flex flex-col items-center gap-2">
                      <p className="text-sm text-green-600 font-medium">File uploaded successfully</p>
                      <a
                        href={modelAnswerUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-primary underline break-all"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {modelAnswerUrl}
                      </a>
                      <p className="text-xs text-gray-400">Click to replace</p>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">Click or drag & drop a PDF</p>
                  )}
                </div>
              </Upload>
            </div>

            <div className="flex justify-end pt-4">
              <Button
                type={ButtonTypes.SUBMIT}
                variant="primary"
                className="!h-12 w-40 !text-sm font-bold rounded-md !bg-[#4379EE] hover:!bg-primary"
                disabled={isSubmitting || isUploading}
              >
                {isSubmitting ? (
                  <Spinner />
                ) : isEditMode ? (
                  "Update Exam"
                ) : (
                  "Create Exam"
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateIFRSExam;